import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { Button } from '@platform/ui';
import { cx } from '@platform/core/lib/cx';
import { catalogLabel, groupModules, navGroups } from '@platform/navigation';
import type { IProfileTabProps } from './IProfileTabProps';
import { usePins } from './usePins';
import { useShellNav } from './useShellNav';
import { useWorkspaceMeta } from './useWorkspaceMeta';

const MAX_PINS = 6;

/**
 * Вкладка «Верхнє меню» профілю (прототип v4): до 6 закріплених модулів робочого простору — лічильник «Закріплено N з 6», «Скинути до типових»,
 * порядок (‹ ›) на бренд-синій смузі й сітка модулів із прапорцями (по досягненні ліміту решта вимкнена).
 */
export function ProfilePins({ ws }: IProfileTabProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const { catalog } = useShellNav(ws);
  const { workspace } = useWorkspaceMeta(ws);
  const [pinned, , save] = usePins(ws);
  const name = (key: string) => label(catalog?.modules.find((m) => m.key === key)?.name) || key;
  const move = (i: number, d: number) => { const l = [...pinned]; [l[i], l[i + d]] = [l[i + d]!, l[i]!]; save(l); };
  const seen = new Set<string>();
  // модулі груп (з власними модулями простору — groupModules), кожен один раз
  const modules = navGroups().flatMap((g) => groupModules(g).filter((k) => !k.includes('/') && !seen.has(k) && seen.add(k) && catalog?.modules.some((m) => m.key === k && m.installed))
    .map((key) => ({ key, group: label(catalogLabel(g)) })));
  return (
    <div className="pf-pins">
      <div className="pf-pins__head">
        <div className="pf-pins__titles"><strong>{t('profilePage.menuTitle')}</strong><span className="muted">{t('profilePage.menuHint', { max: MAX_PINS, ws: workspace?.name ?? '' })}</span></div>
        <div className="pf-pins__actions">
          <span className={cx('pf-pins__count', pinned.length >= MAX_PINS && 'is-full')}>{t('profilePage.pinCount', { count: pinned.length, max: MAX_PINS })}</span>
          <Button size="sm" onClick={() => save([])}>{t('profilePage.reset')}</Button>
        </div>
      </div>
      {pinned.length > 0 && (
        <>
          <span className="t-label">{t('profilePage.order')}</span>
          <div className="pf-order">
            {pinned.map((key, i) => (
              <span key={key} className="pf-order__pill">{name(key)}
                <button type="button" aria-label={`${t('profilePage.moveLeft')}: ${name(key)}`} disabled={i === 0} onClick={() => move(i, -1)}>‹</button>
                <button type="button" aria-label={`${t('profilePage.moveRight')}: ${name(key)}`} disabled={i === pinned.length - 1} onClick={() => move(i, 1)}>›</button>
              </span>
            ))}
          </div>
        </>
      )}
      <div className="pf-grid">
        {modules.map(({ key, group }) => {
          const on = pinned.includes(key);
          const disabled = !on && pinned.length >= MAX_PINS;
          return (
            <label key={key} className={cx('pf-mod', on && 'is-on', disabled && 'is-disabled')}>
              <input type="checkbox" checked={on} disabled={disabled} onChange={() => save(on ? pinned.filter((k) => k !== key) : [...pinned, key])} />
              <span className="pf-mod__text"><span className="pf-mod__name">{name(key)}</span><span className="pf-mod__group">{group}</span></span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
